import { Form, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNextMeetingType, serviceFromZip } from 'commons/libs/meeting_utils';
import { REQUEST_STATUSES } from 'commons/types/common';
import { MEETING_STATUSES, MEETING_TYPES } from 'commons/types/meetingsTypes';
import { defaultSchedule } from 'components/schedule/ScheduleType';
import { useAppDispatch, useAppSelector } from 'stores/hooks';
import {
  getCreateMeetingRequestStatus,
  getNextMeeting,
  resetCurrentMeeting,
} from 'stores/meetings/meetingsSlice';
import { useServices, useUser } from 'stores/user/userHooks';
import { setUserTimerAsync } from 'stores/user/userSlice';
import { useMeetingList } from 'stores/meetings/meetingsHooks';
import SchedulePsad from './SchedulePsad';
import ScheduleDefault from './ScheduleDefault';
import ScheduleRDV2 from './ScheduleRDV2';

const SCHEDULE_STEPS = {
  date_choice: 'date',
  cgu: 'cgu',
};
export default function Schedule() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [user] = useUser();
  const [meetings] = useMeetingList();

  // Needed to get the last meeting to user do
  const nextMeeting = useAppSelector(getNextMeeting);
  const [form] = Form.useForm();
  const [step, setStep] = useState<string>(SCHEDULE_STEPS.date_choice);
  const [services] = useServices();
  const createMeetingRequestStatus = useAppSelector(
    getCreateMeetingRequestStatus,
  );
  const nextRdvType = useMemo(
    () =>
      getNextMeetingType(
        nextMeeting,
        user?.patient?.metadata.status,
        user?.patient?.metadata.skipToRDV2,
        user?.patient?.is_psad,
      ),
    [
      nextMeeting,
      user?.patient?.metadata.skipToRDV2,
      user?.patient?.metadata.status,
      user?.patient?.is_psad,
    ],
  );
  const defaultScheduledForm = defaultSchedule(
    nextRdvType,
    serviceFromZip(services, user?.patient?.zip || ''),
  );
  const onChangeStep = useCallback(
    (nextStep: string) => () => {
      setStep(nextStep);
    },
    [],
  );

  useEffect(() => form.resetFields(), [form]);

  useEffect(() => {
    if (user?.patient.timer_start && !user.patient.timer_end) {
      dispatch(setUserTimerAsync(user.patient.id));
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (createMeetingRequestStatus === REQUEST_STATUSES.SUCCESS) {
      dispatch(resetCurrentMeeting());
      navigate(`/?state=to_justify_${nextMeeting?.type || 'RDV_1'}`);
    } else if (createMeetingRequestStatus === REQUEST_STATUSES.ERROR) {
      dispatch(resetCurrentMeeting());
      navigate('/?state=error_scheduling');
    }
  }, [
    navigate,
    createMeetingRequestStatus,
    user?.email,
    nextMeeting,
    dispatch,
  ]);

  const isFirst = useMemo(() => {
    return (
      meetings?.filter(
        (meeting) => meeting.status === MEETING_STATUSES.ACCEPTED,
      )?.length === 0
    );
  }, [meetings]);

  if (
    createMeetingRequestStatus === REQUEST_STATUSES.PENDING ||
    nextRdvType === MEETING_TYPES.None
  ) {
    return <Spin />;
  }

  if (nextRdvType === MEETING_TYPES.RDV_2) {
    return (
      <ScheduleRDV2
        user={user}
        form={form}
        defaultScheduledForm={defaultScheduledForm}
        step={step}
        hideCGUs={isFirst}
        onChangeStep={onChangeStep}
      />
    );
  }

  if (nextRdvType === MEETING_TYPES.RDV_SWITCH) {
    return (
      <SchedulePsad
        patient={user?.patient}
        form={form}
        defaultScheduledForm={defaultScheduledForm}
        step={step}
        hideCGUs={isFirst}
        onChangeStep={onChangeStep}
      />
    );
  }

  return (
    <ScheduleDefault
      patient={user?.patient}
      form={form}
      defaultScheduledForm={defaultScheduledForm}
      step={step}
      hideCGUs={isFirst}
      nextRdvType={nextRdvType}
      nextMeeting={nextMeeting}
      onChangeStep={onChangeStep}
    />
  );
}
